import { Box, Container, Grid2 } from "@mui/material";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PageActions from "../components/Common/Buttons/PageActions";
import ErrorMessage from "../components/Common/ErrorMessage";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import PageTitle from "../components/Common/PageTitle";
import MenuPlanResponsive from "../components/MenuPlan/MenuPlanResponsive";
import RecipeSelectModal from "../components/Modals/RecipeSelectModal";
import { MarkedRecipeContext } from "../contexts/MarkedRecipesContext";
import { MenuPlanContext } from "../contexts/MenuPlanContext";
import { RecipeContext } from "../contexts/RecipeContext";
import useDocumentTitle from "../hooks/useDocumentTitle";
import useMenuPlanTableData from "../hooks/useMenuPlanTableData";
import { flexCenter, flexColumn } from "../styles/commonStyles";

const MenuPlanPage = () => {
  const { recipes = [] } = useContext(RecipeContext);
  const { markedRecipeIds } = useContext(MarkedRecipeContext);
  const { menuPlans = [], setMenuPlans, loading, error, saveMenuPlans } = useContext(MenuPlanContext);
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedMeal, setSelectedMeal] = useState("");
  useDocumentTitle("menuPlanTitle");

  const markedRecipes = useMemo(() => markedRecipeIds.map((id) => recipes.find((recipe) => recipe.id === id)).filter(Boolean), [markedRecipeIds, recipes]);

  const unmarkedRecipes = useMemo(() => recipes.filter((recipe) => !markedRecipeIds.includes(recipe.id)), [markedRecipeIds, recipes]);
  const { tableHeaders, tableRows } = useMenuPlanTableData(menuPlans, recipes);

  const handleOpenModal = useCallback((day, meal) => {
    setSelectedDay(day);
    setSelectedMeal(meal);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleSave = useCallback(
    (recipeIds) => {
      if (recipeIds.length > 0 && selectedDay && selectedMeal) {
        const newMenuPlanItems = [];

        recipeIds.forEach((recipeId) => {
          const isDuplicate = menuPlans.some((item) => item.day === selectedDay && item.meal === selectedMeal && item.recipeId === recipeId);

          // Only add if it's not a duplicate
          if (!isDuplicate) {
            newMenuPlanItems.push({
              recipeId,
              day: selectedDay,
              meal: selectedMeal,
            });
          }
        });

        if (newMenuPlanItems.length > 0) {
          const newMenuPlan = [...menuPlans, ...newMenuPlanItems];
          setMenuPlans(newMenuPlan);
          saveMenuPlans(newMenuPlan);
        }
      }
      handleCloseModal();
    },
    [selectedDay, selectedMeal, menuPlans, setMenuPlans, saveMenuPlans, handleCloseModal],
  );

  const handleShare = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/shareMenuPlan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ menuPlan: menuPlans }),
      });

      const data = await response.json();
      if (data.shareUrl) {
        await navigator.clipboard.writeText(data.shareUrl);
        return true;
      }
    } catch (error) {
      console.error("Error sharing menu plan:", error);
    }
    return false;
  };

  const handleRemoveFromMenuPlan = useCallback(
    (recipeId, day, meal) => {
      const updatedMenuPlan = menuPlans.filter((item) => !(item.day === day && item.meal === meal && item.recipeId === recipeId));
      setMenuPlans(updatedMenuPlan);
      saveMenuPlans(updatedMenuPlan);
    },
    [menuPlans, setMenuPlans, saveMenuPlans],
  );

  const handleReset = useCallback(() => {
    setMenuPlans([]);
    saveMenuPlans([]);
  }, [saveMenuPlans, setMenuPlans]);

  const isDisabled = menuPlans.length === 0;

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage error={error?.message || t("errorLoading")} />;

  return (
    <Container sx={flexColumn}>
      <Box sx={flexCenter}>
        <PageTitle text={t("weeklyMenuPlan")} />

        <PageActions onReset={handleReset} onShare={handleShare} tableHeaders={tableHeaders} tableRows={tableRows} fileName={t("weeklyMenuPlan")} isDisabled={isDisabled} />
      </Box>
      <Grid2 xs={12}>
        <MenuPlanResponsive menuPlan={menuPlans} recipes={recipes} handleOpenModal={handleOpenModal} handleRemoveFromMenuPlan={handleRemoveFromMenuPlan}></MenuPlanResponsive>
      </Grid2>

      <RecipeSelectModal open={modalOpen} handleClose={handleCloseModal} recipes={unmarkedRecipes} markedRecipes={markedRecipes} handleSave={handleSave} />
    </Container>
  );
};

export default MenuPlanPage;
